import React from "react";

import type { T_UsageFeatureStats } from "@lattice/core-lib/types/usage";
import { projectUsageFeature } from "@lattice/shared-frontend/usage";
import { Surface } from "@lattice/frontend/components/kit/Surface";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import { SettingsPanelError, SettingsPanelLoading } from "./SettingsPanelState";

export function UsagePanelView({
  features,
}: {
  features: readonly T_UsageFeatureStats[];
}) {
  if (features.length === 0) {
    return (
      <Surface tone="subtle">
        <p className="shell-settings__body">No usage data is available yet.</p>
      </Surface>
    );
  }

  return (
    <div className="shell-settings-card-stack">
      {features.map((feature) => {
        const projection = projectUsageFeature(feature);
        return (
          <Surface key={projection.feature} tone="subtle">
            <div className="shell-settings-usage__header">
              <span>{projection.displayName}</span>
              <span>{projection.usedLabel}</span>
            </div>
            {projection.limit !== null ? (
              <div
                aria-label={`${projection.displayName} usage`}
                aria-valuemax={projection.limit}
                aria-valuemin={0}
                aria-valuenow={projection.used}
                className="shell-settings-usage__track"
                role="progressbar"
              >
                <span style={{ width: `${projection.percentUsed}%` }} />
              </div>
            ) : null}
            <div className="shell-settings-usage__meta">
              <span>{projection.remainingLabel}</span>
              <span>{projection.resetLabel}</span>
            </div>
            {projection.bonusLabel ? (
              <p className="shell-settings-usage__bonus">
                {projection.bonusLabel}
              </p>
            ) : null}
          </Surface>
        );
      })}
    </div>
  );
}

export function UsagePanel() {
  const usage = trpcApi.usage.get_usage_stats.useQuery();
  if (usage.isPending) {
    return <SettingsPanelLoading label="Loading usage" />;
  }
  if (usage.isError || !usage.data) {
    return <SettingsPanelError message="Usage could not be loaded." />;
  }
  return <UsagePanelView features={usage.data.features} />;
}
